import React from 'react';
import styles from './StrategyForm.module.css';
import ConditionBuilder from '../ConditionBuilder/ConditionBuilder';
import Button from '../../common/Button/Button';
import NotificationGroupDropdown from './NotificationGroupDropdown';
import ModeMultiSelect, { NOTIFY_TARGET_MODES } from './ModeMultiSelect';
import {
  SIZING_QTY_MIN, SIZING_QTY_MAX,
  SIZING_CAPITAL_MIN, SIZING_CAPITAL_MAX,
  TSL_PCT_MIN, TSL_PCT_MAX,
  TARGET_PCT_MIN, TARGET_PCT_MAX,
  TARGET_PRICE_MIN, TARGET_PRICE_MAX,
  BROKERAGE_PCT_MIN, BROKERAGE_PCT_MAX,
} from './constants';

/**
 * Step 2 — Strategy Logic: action, mode, entry/exit conditions,
 * stop loss, target, brokerage, and position sizing.
 *
 * Purely presentational. All state and handlers live in the parent
 * StrategyForm orchestrator and are passed down as props.
 */
const StepStrategyLogic = ({
  formData,
  updateField,
  strategyConfig,
  // Sizing
  setSizingType,
  setSizingValue,
  // Trailing stop loss
  setTslType,
  setTslValue,
  // Target
  setTargetType,
  setTargetValue,
  // Brokerage
  setBrokerageEnabled,
  setBrokerageValue,
  // Notifications
  notificationGroups = [],
  // Trade Type options for "Create Recommendation" mode
  tradeTypes = [],
}) => {
  const recoSelected = (formData.alert_or_trade || []).includes('CreateRecommendation');
  const recoGroups = formData.reco_notification_group_ids || [];
  // "Create Recommendation" audience picker — UI only. The backend already
  // decides creator-vs-groups purely from whether reco_notification_group_ids is
  // empty (recommendationAutomation.service.js), so this toggle adds no new
  // field/column: 'notifyMe' = creator only (groups cleared); 'recipients' =
  // chosen groups. Initial value is derived from existing groups so editing a
  // saved strategy lands on the right tab — Step 2 only mounts when navigated
  // to, so this lazy init reads the already-prefilled groups.
  const [recoTarget, setRecoTarget] = React.useState(
    recoGroups.length > 0 ? 'recipients' : 'notifyMe',
  );

  // Pick the recommendation audience from the Mode dropdown's sub-options.
  // Either choice also ensures "Create Recommendation" itself is selected (so
  // clicking a sub-option checks the parent). "Notify me" clears any chosen
  // groups so the audience collapses to the creator (the backend's empty-groups
  // fallback); "Select groups" reveals the Reco Recipients dropdown.
  const pickRecoTarget = (target) => {
    setRecoTarget(target);
    if (target === 'notifyMe' && recoGroups.length) {
      updateField('reco_notification_group_ids', []);
    }
    const modes = Array.isArray(formData.alert_or_trade) ? formData.alert_or_trade : [];
    if (!modes.includes('CreateRecommendation')) {
      updateField('alert_or_trade', [...modes, 'CreateRecommendation']);
    }
  };

  return (
  <div className={styles.stepContent}>
    <h2 className={styles.stepTitle}>Strategy Logic</h2>
    <p className={styles.stepDescription}>
      {formData.name
        ? <>Configure conditions for <strong style={{ color: 'var(--primary-blue)' }}>{formData.name}</strong></>
        : 'Define conditions, action, and mode.'}
    </p>

    <div className={styles.fieldRow}>
      {/* Action — BUY/SELL side by side horizontally, compact width. */}
      <div className={styles.fieldGroup} style={{ flex: 'none' }}>
        <label className={styles.label}>Action *</label>
        <div className={styles.actionToggle}>
          <button
            type="button"
            className={`${styles.actionBtn} ${formData.action === 'BUY' ? styles.buyActive : ''}`}
            onClick={() => updateField('action', 'BUY')}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>trending_up</span>
            BUY
          </button>
          <button
            type="button"
            className={`${styles.actionBtn} ${formData.action === 'SELL' ? styles.sellActive : ''}`}
            onClick={() => updateField('action', 'SELL')}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>trending_down</span>
            SELL
          </button>
        </div>
      </div>

      {/* Selector group is pinned to the RIGHT end of the row — Mode is always
          the right-most item (mirroring the Action pinned at the left end). When
          modes are selected, their dropdowns (Trade Type / Reco Recipients /
          Groups) appear to the LEFT of Mode. The widened form keeps all of them
          on one line; flex-wrap is only a safety net for very narrow viewports. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start', justifyContent: 'flex-end', marginLeft: 'auto' }}>

      {/* Trade Type selector — shown whenever "Create Recommendation" is
          selected (both audiences need a trade type). Single-select; mirrors the
          Groups selector's fixed width + reserved warning slot so the row never
          reflows. */}
      {recoSelected && (
        <div className={styles.fieldGroup} style={{ flex: 'none', width: 165 }}>
          <label className={styles.label}>Trade Type</label>
          <select
            className={styles.select}
            value={formData.reco_trade_type_id ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              updateField('reco_trade_type_id', v === '' ? null : Number(v));
            }}
          >
            <option value="">Select type</option>
            {tradeTypes.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          {/* Fixed-height slot so toggling the warning never shifts the row. */}
          <div style={{ minHeight: 18, marginTop: 4 }}>
            {(formData.reco_trade_type_id == null) && (
              <span className={styles.sizingHint} style={{ color: 'var(--alert-warning)' }}>
                Select a trade type.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Recommendation Recipients — the draft's OWN audience, shown only on the
          "Select groups" tab. Independent of Group Notification. With the toggle
          on this tab, an empty selection is an INCOMPLETE state (it would silently
          fall back to creator-only), so it warns to pick at least one group. */}
      {recoSelected && recoTarget === 'recipients' && (
        <div className={styles.fieldGroup} style={{ flex: 'none', width: 165 }}>
          <label className={styles.label}>Reco Recipients</label>
          <NotificationGroupDropdown
            groups={notificationGroups}
            selected={formData.reco_notification_group_ids}
            onChange={(ids) => updateField('reco_notification_group_ids', ids)}
          />
          {/* Fixed-height slot so toggling the warning never shifts the row. */}
          <div style={{ minHeight: 18, marginTop: 4 }}>
            {recoGroups.length === 0 && (
              <span className={styles.sizingHint} style={{ color: 'var(--alert-warning)' }}>
                Select at least one group.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Group selector — fixed width (matches Mode), sits immediately to its
          LEFT, shown only when "Group Notification" is among the selected modes.
          Fixed width + reserved warning height prevent any layout thrash when
          selecting/unselecting groups. */}
      {(formData.alert_or_trade || []).includes('Notification') && (
        <div className={styles.fieldGroup} style={{ flex: 'none', width: 165 }}>
          <label className={styles.label}>Groups</label>
          <NotificationGroupDropdown
            groups={notificationGroups}
            selected={formData.notification_group_ids}
            onChange={(ids) => updateField('notification_group_ids', ids)}
          />
          {/* Fixed-height slot so toggling the warning never shifts the row. */}
          <div style={{ minHeight: 18, marginTop: 4 }}>
            {notificationGroups.length > 0 && formData.notification_group_ids.length === 0 && (
              <span className={styles.sizingHint} style={{ color: 'var(--alert-warning)' }}>
                Select at least one group.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Mode — multi-select. Modes combine (e.g. Signal + Trade); the two
          notification targets (Group Notification / Notify me) are mutually
          exclusive. Picking "Group Notification" reveals the group selector to
          its left; "Notify me" needs no group (audience = the strategy creator). */}
      <div className={styles.fieldGroup} style={{ flex: 'none', width: 165 }}>
        <label className={styles.label}>Mode</label>
        <ModeMultiSelect
          selected={Array.isArray(formData.alert_or_trade) ? formData.alert_or_trade : []}
          recoTarget={recoTarget}
          onPickRecoTarget={pickRecoTarget}
          onChange={(modes) => {
            const wasReco = (formData.alert_or_trade || []).includes('CreateRecommendation');
            const isReco = modes.includes('CreateRecommendation');
            updateField('alert_or_trade', modes);
            // notification_enabled = any notification target is selected.
            updateField(
              'notification_enabled',
              modes.some((m) => NOTIFY_TARGET_MODES.includes(m)),
            );
            // Dropping "Group Notification" clears any chosen groups so a stale
            // audience can't linger on a strategy that no longer notifies groups.
            if (!modes.includes('Notification')) {
              updateField('notification_group_ids', []);
            }
            if (!isReco) {
              // Dropping "Create Recommendation" clears its trade type + recipients.
              updateField('reco_trade_type_id', null);
              updateField('reco_notification_group_ids', []);
            } else if (!wasReco) {
              // Newly enabled via the parent checkbox → default to "Notify me".
              setRecoTarget('notifyMe');
            }
          }}
        />
      </div>
      </div>
    </div>

    <ConditionBuilder
      conditions={formData.conditions}
      onChange={(conditions) => updateField('conditions', conditions)}
      label="Entry Conditions"
      config={strategyConfig}
    />

    <div className={styles.exitSection}>
      <ConditionBuilder
        conditions={formData.exit_conditions}
        onChange={(conditions) => updateField('exit_conditions', conditions)}
        label="Exit Conditions (Optional)"
        config={strategyConfig}
      />
    </div>

    <div className={styles.sizingSection}>
      <h3 className={styles.sizingTitle}>Stop Loss / Trailing Stop Loss <span className={styles.optionalTag}>(Optional)</span></h3>
      <p className={styles.sizingHint}>
        An exit rule based on price. Choose a fixed stop loss, or a stepped trailing stop that locks in profits as price moves.
      </p>

      <div className={styles.sizingTypeRow} role="radiogroup" aria-label="Stop loss type">
        <button
          type="button"
          role="radio"
          aria-checked={!formData.trailing_stop_loss}
          className={`${styles.sizingChip} ${!formData.trailing_stop_loss ? styles.sizingChipActive : ''}`}
          onClick={() => setTslType(null)}
        >
          Not Configured
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={formData.trailing_stop_loss?.type === 'fixed'}
          className={`${styles.sizingChip} ${formData.trailing_stop_loss?.type === 'fixed' ? styles.sizingChipActive : ''}`}
          onClick={() => setTslType('fixed')}
        >
          Fixed Stop Loss
        </button>
        {formData.trailing_stop_loss?.type === 'percent' && (
          <button
            type="button"
            role="radio"
            aria-checked={formData.trailing_stop_loss?.type === 'percent'}
            className={`${styles.sizingChip} ${styles.sizingChipActive}`}
            onClick={() => setTslType('percent')}
          >
            Legacy % Trail
          </button>
        )}
        <button
          type="button"
          role="radio"
          aria-checked={formData.trailing_stop_loss?.type === 'stepped'}
          className={`${styles.sizingChip} ${formData.trailing_stop_loss?.type === 'stepped' ? styles.sizingChipActive : ''}`}
          onClick={() => setTslType('stepped')}
        >
          TSL
        </button>
      </div>

      {formData.trailing_stop_loss?.type === 'fixed' && (
        <div className={`${styles.fieldGroup} ${styles.revealField}`} style={{ marginTop: 'var(--spacing-md)' }}>
          <label className={styles.label}>Stop Loss % ({TSL_PCT_MIN}–{TSL_PCT_MAX})</label>
          <input
            type="number"
            className={styles.input}
            min={TSL_PCT_MIN}
            max={TSL_PCT_MAX}
            step="0.1"
            value={formData.trailing_stop_loss.value ?? ''}
            onChange={(e) => setTslValue('value', e.target.value)}
            placeholder="e.g., 2"
          />
          <p className={styles.sizingHint}>
            Exit if price moves this % against entry. A static stop — it does not trail.
          </p>
        </div>
      )}

      {formData.trailing_stop_loss?.type === 'percent' && (
        <div className={`${styles.fieldGroup} ${styles.revealField}`} style={{ marginTop: 'var(--spacing-md)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label className={styles.label}>
              Legacy Trail percentage ({TSL_PCT_MIN}–{TSL_PCT_MAX})
            </label>
            <Button 
              variant="outline" 
              size="small" 
              onClick={() => setTslType('stepped')}
            >
              Upgrade to TSL
            </Button>
          </div>
          <input
            type="number"
            className={styles.input}
            min={TSL_PCT_MIN}
            max={TSL_PCT_MAX}
            step="0.1"
            value={formData.trailing_stop_loss.value ?? ''}
            onChange={(e) => setTslValue('value', e.target.value)}
            placeholder="e.g., 2"
          />
          <p className={styles.sizingHint}>
            <span style={{ color: 'var(--alert-warning)', fontWeight: 600 }}>Note:</span> This is the legacy sliding TSL. We highly recommend upgrading to TSL for more granular control.
          </p>
        </div>
      )}

      {formData.trailing_stop_loss?.type === 'stepped' && (
        <div className={styles.revealField} style={{ marginTop: 'var(--spacing-md)', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--spacing-md)' }}>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Initial Stop Loss %</label>
            <input
              type="number"
              className={styles.input}
              min="0.1"
              step="0.1"
              value={formData.trailing_stop_loss.initial_stop_pct ?? ''}
              onChange={(e) => setTslValue('initial_stop_pct', e.target.value)}
              placeholder="e.g., 2"
            />
            <p className={styles.sizingHint}>Fixed stop loss from entry until Hurdle is reached.</p>
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Hurdle %</label>
            <input
              type="number"
              className={styles.input}
              min="0.1"
              step="0.1"
              value={formData.trailing_stop_loss.hurdle_pct ?? ''}
              onChange={(e) => setTslValue('hurdle_pct', e.target.value)}
              placeholder="e.g., 5"
            />
            <p className={styles.sizingHint}>Price movement required before TSL activates.</p>
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Step %</label>
            <input
              type="number"
              className={styles.input}
              min="0.1"
              step="0.1"
              value={formData.trailing_stop_loss.step_pct ?? ''}
              onChange={(e) => setTslValue('step_pct', e.target.value)}
              placeholder="e.g., 1"
            />
            <p className={styles.sizingHint}>Stop loss increments for every full step.</p>
          </div>
        </div>
      )}
    </div>

    {/* Target / Take Profit — exit RULE. Checked after stop loss,
        before exit conditions, in both backtest and live. */}
    <div className={styles.sizingSection}>
      <h3 className={styles.sizingTitle}>Target / Take Profit <span className={styles.optionalTag}>(Optional)</span></h3>
      <p className={styles.sizingHint}>
        Books profit automatically when price reaches the target — above entry for BUY, below entry for SELL.
      </p>

      <div className={styles.sizingTypeRow} role="radiogroup" aria-label="Target type">
        <button
          type="button"
          role="radio"
          aria-checked={!formData.target}
          className={`${styles.sizingChip} ${!formData.target ? styles.sizingChipActive : ''}`}
          onClick={() => setTargetType(null)}
        >
          Not Configured
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={formData.target?.type === 'percent'}
          className={`${styles.sizingChip} ${formData.target?.type === 'percent' ? styles.sizingChipActive : ''}`}
          onClick={() => setTargetType('percent')}
        >
          Target %
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={formData.target?.type === 'price'}
          className={`${styles.sizingChip} ${formData.target?.type === 'price' ? styles.sizingChipActive : ''}`}
          onClick={() => setTargetType('price')}
        >
          Fixed Price
        </button>
      </div>

      {formData.target?.type === 'percent' && (
        <div className={`${styles.fieldGroup} ${styles.revealField}`} style={{ marginTop: 'var(--spacing-md)' }}>
          <label className={styles.label}>Target % ({TARGET_PCT_MIN}–{TARGET_PCT_MAX})</label>
          <input
            type="number"
            className={styles.input}
            min={TARGET_PCT_MIN}
            max={TARGET_PCT_MAX}
            step="0.1"
            value={formData.target.value ?? ''}
            onChange={(e) => setTargetValue(e.target.value)}
            placeholder="e.g., 5"
          />
          <p className={styles.sizingHint}>Profit booked when price moves this % in your favour from entry.</p>
        </div>
      )}

      {formData.target?.type === 'price' && (
        <div className={`${styles.fieldGroup} ${styles.revealField}`} style={{ marginTop: 'var(--spacing-md)' }}>
          <label className={styles.label}>Target Price (₹)</label>
          <input
            type="number"
            className={styles.input}
            min={TARGET_PRICE_MIN}
            step="0.05"
            value={formData.target.value ?? ''}
            onChange={(e) => setTargetValue(e.target.value)}
            placeholder="e.g., 110"
          />
          <p className={styles.sizingHint}>Exit at this exact price. Only triggers for trades whose price actually reaches it.</p>
        </div>
      )}
    </div>

    {/* Brokerage & Taxes — cost CONFIG applied per leg as a %
        of trade value so backtest P&L comes out net of costs.
        Lives here (below Target) instead of the detail page so
        all trade economics are configured in one place. */}
    <div className={styles.sizingSection}>
      <h3 className={styles.sizingTitle}>Brokerage &amp; Taxes <span className={styles.optionalTag}>(Optional)</span></h3>
      <p className={styles.sizingHint}>
        Per-side costs as a percent of trade value. Leave as "Not Configured" and backtest results show gross P&amp;L without costs deducted.
      </p>

      <div className={styles.sizingTypeRow} role="radiogroup" aria-label="Brokerage and taxes">
        <button
          type="button"
          role="radio"
          aria-checked={!formData.brokerage_config}
          className={`${styles.sizingChip} ${!formData.brokerage_config ? styles.sizingChipActive : ''}`}
          onClick={() => setBrokerageEnabled(false)}
        >
          Not Configured
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={!!formData.brokerage_config}
          className={`${styles.sizingChip} ${formData.brokerage_config ? styles.sizingChipActive : ''}`}
          onClick={() => setBrokerageEnabled(true)}
        >
          Percent of Trade Value
        </button>
      </div>

      {formData.brokerage_config && (
        <div className={styles.revealField} style={{ marginTop: 'var(--spacing-md)', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--spacing-md)' }}>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Buy Side % ({BROKERAGE_PCT_MIN}–{BROKERAGE_PCT_MAX})</label>
            <input
              type="number"
              className={styles.input}
              min={BROKERAGE_PCT_MIN}
              max={BROKERAGE_PCT_MAX}
              step="0.01"
              value={formData.brokerage_config.buy_pct ?? ''}
              onChange={(e) => setBrokerageValue('buy_pct', e.target.value)}
              placeholder="e.g., 0.03"
            />
            <p className={styles.sizingHint}>Charged on entry, as a % of buy value.</p>
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Sell Side % ({BROKERAGE_PCT_MIN}–{BROKERAGE_PCT_MAX})</label>
            <input
              type="number"
              className={styles.input}
              min={BROKERAGE_PCT_MIN}
              max={BROKERAGE_PCT_MAX}
              step="0.01"
              value={formData.brokerage_config.sell_pct ?? ''}
              onChange={(e) => setBrokerageValue('sell_pct', e.target.value)}
              placeholder="e.g., 0.13"
            />
            <p className={styles.sizingHint}>Charged on exit, as a % of sell value.</p>
          </div>
        </div>
      )}
    </div>

    {/* Position Sizing — required for backtest P&L. If
        left at "Not Configured", backtests still run but
        P&L columns show a "not configured" placeholder
        instead of fake zeros. */}
    <div className={styles.sizingSection}>
      <h3 className={styles.sizingTitle}>Position Sizing</h3>
      <p className={styles.sizingHint}>
        How many shares to trade per signal. Required for
        accurate backtest P&amp;L. Leave as "Not Configured" to
        skip P&amp;L reporting.
      </p>

      <div className={styles.sizingTypeRow} role="radiogroup" aria-label="Position sizing type">
        <button
          type="button"
          role="radio"
          aria-checked={!formData.position_sizing}
          className={`${styles.sizingChip} ${!formData.position_sizing ? styles.sizingChipActive : ''}`}
          onClick={() => setSizingType(null)}
        >
          Not Configured
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={formData.position_sizing?.type === 'fixed_quantity'}
          className={`${styles.sizingChip} ${formData.position_sizing?.type === 'fixed_quantity' ? styles.sizingChipActive : ''}`}
          onClick={() => setSizingType('fixed_quantity')}
        >
          Fixed Quantity
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={formData.position_sizing?.type === 'fixed_capital'}
          className={`${styles.sizingChip} ${formData.position_sizing?.type === 'fixed_capital' ? styles.sizingChipActive : ''}`}
          onClick={() => setSizingType('fixed_capital')}
        >
          Fixed Capital
        </button>
      </div>

      {formData.position_sizing && (
        <div className={`${styles.fieldGroup} ${styles.revealField}`} style={{ marginTop: 'var(--spacing-md)' }}>
          <label className={styles.label}>
            {formData.position_sizing.type === 'fixed_quantity'
              ? `Shares per trade (${SIZING_QTY_MIN}–${SIZING_QTY_MAX.toLocaleString('en-IN')})`
              : `Capital per trade in ₹ (${SIZING_CAPITAL_MIN}–${SIZING_CAPITAL_MAX.toLocaleString('en-IN')})`}
          </label>
          <input
            type="number"
            className={styles.input}
            min={formData.position_sizing.type === 'fixed_quantity' ? SIZING_QTY_MIN : SIZING_CAPITAL_MIN}
            max={formData.position_sizing.type === 'fixed_quantity' ? SIZING_QTY_MAX : SIZING_CAPITAL_MAX}
            step={formData.position_sizing.type === 'fixed_quantity' ? 1 : 100}
            value={formData.position_sizing.value ?? ''}
            onChange={(e) => setSizingValue(e.target.value)}
            placeholder={formData.position_sizing.type === 'fixed_quantity' ? 'e.g., 100' : 'e.g., 50000'}
          />
          <p className={styles.sizingHint}>
            {formData.position_sizing.type === 'fixed_quantity'
              ? 'Whole shares only. Each signal will trade exactly this many shares.'
              : 'Each trade deploys this rupee amount. Quantity = floor(capital ÷ entry price). If the capital is not enough for one share, the trade is skipped.'}
          </p>
        </div>
      )}
    </div>
  </div>
  );
};

export default StepStrategyLogic;
